import { useEffect, useState } from "react";
import type { ElementType, ReactNode } from "react";
import {
  Monitor,
  Moon,
  Sun,
  Globe,
  Clock,
  LayoutTemplate,
  AreaChart,
  MoveVertical,
} from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";
import { useAppLayout, type AppLayoutMode } from "@/hooks/useAppLayout";
import { useTheme } from "@/hooks/useTheme";
import { useNexusVersion } from "@/hooks/useNexusVersion";
import { useAppContext } from "@/contexts/AppContext";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useAccountSettings } from "@/contexts/AccountSettingsContext";
import { Button, SegmentedControl, TabbedDialog } from "@nexus/ui";
import type { AnalyticsChartHeight, DetectionRepresentation } from "@/lib/live/visualPreferences";

interface Props {
  onClose: () => void;
}

const THEME_OPTIONS = [
  { value: "light", icon: Sun, label: "Light" },
  { value: "system", icon: Monitor, label: "System" },
  { value: "dark", icon: Moon, label: "Dark" },
] as const;

const TIMEZONE_OPTIONS = [
  { value: "utc", icon: Globe, label: "UTC" },
  { value: "local", icon: Clock, label: "Local" },
] as const;

const LAYOUT_OPTIONS = [
  {
    value: "workspace",
    label: "Workspace",
    description:
      "A persistent spaces rail with a dedicated sidebar for the current space. Recommended.",
  },
  {
    value: "compact",
    label: "Compact",
    description: "A compact sidebar with an integrated space switcher.",
  },
] as const;

const DETECTION_REPRESENTATION_OPTIONS = [
  { value: "smooth-line", label: "Smooth" },
  { value: "stepped-line", label: "Stepped" },
  { value: "stepped-area", label: "Area" },
] as const;

const CHART_HEIGHT_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "tall", label: "Tall" },
] as const;

type ThemeValue = (typeof THEME_OPTIONS)[number]["value"];
type TimezoneValue = (typeof TIMEZONE_OPTIONS)[number]["value"];
type LayoutValue = (typeof LAYOUT_OPTIONS)[number]["value"];

export function SettingsModal({ onClose }: Props) {
  const { isClientProfile } = useAuthorization();
  const accountSettings = useAccountSettings();
  const { data: nexusVersion } = useNexusVersion();
  const { theme: ctxTheme, setTheme } = useTheme();
  const {
    timezone: ctxTimezone,
    setTimezone,
    detectionRepresentation: ctxDetectionRepresentation,
    setDetectionRepresentation,
    analyticsChartHeight: ctxAnalyticsChartHeight,
    setAnalyticsChartHeight,
  } = useAppContext();
  const { layoutMode: ctxLayoutMode, setLayoutMode } = useAppLayout();

  // Optimistic local state so controls highlight instantly.
  const [theme, setThemeLocal] = useState(ctxTheme);
  const [timezone, setTimezoneLocal] = useState(ctxTimezone);
  const [layoutMode, setLayoutModeLocal] = useState(ctxLayoutMode);
  const [detectionRepresentation, setDetectionRepresentationLocal] = useState(
    ctxDetectionRepresentation,
  );
  const [analyticsChartHeight, setAnalyticsChartHeightLocal] = useState(ctxAnalyticsChartHeight);

  useEffect(() => {
    setThemeLocal(ctxTheme);
  }, [ctxTheme]);

  useEffect(() => {
    setTimezoneLocal(ctxTimezone);
  }, [ctxTimezone]);

  useEffect(() => {
    setLayoutModeLocal(ctxLayoutMode);
  }, [ctxLayoutMode]);

  useEffect(() => {
    setDetectionRepresentationLocal(ctxDetectionRepresentation);
  }, [ctxDetectionRepresentation]);

  useEffect(() => {
    setAnalyticsChartHeightLocal(ctxAnalyticsChartHeight);
  }, [ctxAnalyticsChartHeight]);

  function handleSetTheme(value: ThemeValue) {
    setThemeLocal(value);
    setTheme(value);
  }

  function handleSetTimezone(value: TimezoneValue) {
    setTimezoneLocal(value);
    setTimezone(value);
  }

  function handleSetLayoutMode(value: AppLayoutMode) {
    setLayoutModeLocal(value);
    setLayoutMode(value);
  }

  function handleSetDetectionRepresentation(value: DetectionRepresentation) {
    setDetectionRepresentationLocal(value);
    setDetectionRepresentation(value);
  }

  function handleSetAnalyticsChartHeight(value: AnalyticsChartHeight) {
    setAnalyticsChartHeightLocal(value);
    setAnalyticsChartHeight(value);
  }

  return (
    <TabbedDialog
      open
      navigation="sidebar"
      title="Settings"
      description="Changes are applied immediately."
      onClose={onClose}
      closeLabel="Close settings"
      tabsLabel="Settings sections"
      defaultTab="general"
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-3">
          {accountSettings.isError ? (
            <p role="alert" className="w-full text-sm text-status-error-text">
              Settings could not be saved. Your previous settings have been restored. Please try
              again.
            </p>
          ) : accountSettings.isPending ? (
            <p role="status" className="w-full text-sm text-secondary">
              Saving settings...
            </p>
          ) : null}
          {nexusVersion ? (
            <span className="mr-auto text-xs text-muted">Nexus &middot; {nexusVersion}</span>
          ) : null}
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      }
      tabs={[
        {
          value: "general",
          label: "General",
          content: (
            <fieldset
              disabled={!accountSettings.available}
              aria-label="General preferences"
              className="min-w-0 overflow-hidden rounded-xl ring-1 ring-border/40"
            >
              <SettingsRow
                icon={LayoutTemplate}
                title="Layout"
                description="Choose the navigation shell used across the application."
              >
                <LayoutControl value={layoutMode} onChange={handleSetLayoutMode} />
              </SettingsRow>
              <SettingsRow
                icon={Monitor}
                title="Theme"
                description="Choose the visual theme used by the interface."
              >
                <SegmentedControl
                  label="Theme"
                  value={theme}
                  options={THEME_OPTIONS}
                  onChange={handleSetTheme}
                  variant="inset"
                  className="w-full"
                />
              </SettingsRow>
              <SettingsRow
                icon={Clock}
                title="Time display"
                description="Choose whether timestamps use UTC or your device timezone."
              >
                <SegmentedControl
                  label="Time display"
                  value={timezone}
                  options={TIMEZONE_OPTIONS}
                  onChange={handleSetTimezone}
                  variant="inset"
                  className="w-full"
                />
              </SettingsRow>
            </fieldset>
          ),
        },
        {
          value: "live",
          label: "Charts",
          content: (
            <fieldset
              disabled={!accountSettings.available}
              aria-label="Chart preferences"
              className="min-w-0 overflow-hidden rounded-xl ring-1 ring-border/40"
            >
              <SettingsRow
                icon={AreaChart}
                title="Detection volumes"
                description="Choose how cumulative detections are represented over time."
              >
                <SegmentedControl
                  label="Detection volume representation"
                  value={detectionRepresentation}
                  options={DETECTION_REPRESENTATION_OPTIONS}
                  onChange={handleSetDetectionRepresentation}
                  variant="inset"
                  className="w-full"
                />
              </SettingsRow>
              <SettingsRow
                icon={MoveVertical}
                title="Chart height"
                description="Adjust the height of monitoring charts."
              >
                <SegmentedControl
                  label="Analytics chart height"
                  value={analyticsChartHeight}
                  options={CHART_HEIGHT_OPTIONS}
                  onChange={handleSetAnalyticsChartHeight}
                  variant="inset"
                  className="w-full"
                />
              </SettingsRow>
            </fieldset>
          ),
        },
      ].filter((tab) => !isClientProfile || tab.value === "general")}
    />
  );
}
function LayoutControl({
  value,
  onChange,
}: {
  value: LayoutValue;
  onChange: (value: LayoutValue) => void;
}) {
  return (
    <Tooltip.Provider delayDuration={120}>
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
        {LAYOUT_OPTIONS.map((option) => {
          const selected = value === option.value;

          return (
            <Tooltip.Root key={option.value}>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  onClick={() => onChange(option.value)}
                  aria-pressed={selected}
                  className={cn(
                    "group rounded-xl border p-2 text-center transition-colors",
                    selected
                      ? "border-brand-cyan bg-brand-cyan/10 text-primary"
                      : "border-border bg-background/70 text-secondary hover:bg-surface-elevated hover:text-primary",
                  )}
                >
                  <div className="flex justify-center">
                    <LayoutPreview mode={option.value} selected={selected} />
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-2xs font-medium">
                    <span>{option.label}</span>
                  </div>
                </button>
              </Tooltip.Trigger>

              <Tooltip.Portal>
                <Tooltip.Content
                  side="top"
                  sideOffset={8}
                  className="z-70 max-w-55 rounded-lg border border-border bg-surface px-2.5 py-2 text-2xs leading-4 text-secondary shadow-xl"
                >
                  {option.description}
                  <Tooltip.Arrow className="fill-surface" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        })}
      </div>
    </Tooltip.Provider>
  );
}
function LayoutPreview({ mode, selected }: { mode: LayoutValue; selected: boolean }) {
  const accent = selected ? "var(--brand-cyan)" : "var(--text-subtle)";
  const frame = selected ? "var(--text)" : "var(--border-strong)";
  const surface = selected ? "var(--row-selected)" : "var(--surface-nav)";
  const deep = selected ? "var(--surface-topbar)" : "var(--text-subtle)";

  return (
    <svg aria-hidden="true" viewBox="0 0 84 56" className="h-12 w-18 shrink-0 rounded-md">
      <rect
        x="1"
        y="1"
        width="82"
        height="54"
        rx="8"
        fill="var(--surface)"
        stroke={frame}
        strokeWidth="1.5"
      />
      {mode === "compact" && (
        <>
          <rect x="4" y="4" width="12" height="48" rx="4" fill="var(--surface-nav)" />
          <circle cx="10" cy="10" r="2.3" fill="var(--surface)" />
          <rect x="20" y="4" width="60" height="8" rx="3" fill={surface} />
          <rect x="24" y="18" width="50" height="5" rx="2.5" fill={accent} opacity="0.7" />
          <rect x="24" y="28" width="42" height="5" rx="2.5" fill={surface} />
        </>
      )}
      {mode === "workspace" && (
        <>
          <rect x="4" y="4" width="10" height="48" rx="4" fill={deep} />
          <rect x="18" y="4" width="14" height="48" rx="4" fill="var(--surface-nav)" />
          <rect x="35" y="4" width="45" height="8" rx="3" fill={surface} />
          <rect x="39" y="18" width="35" height="5" rx="2.5" fill={accent} opacity="0.7" />
          <rect x="39" y="28" width="28" height="5" rx="2.5" fill={surface} />
        </>
      )}
    </svg>
  );
}

function SettingsRow({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: ElementType;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-stretch justify-between gap-3 border-b border-border/35 bg-surface px-4 py-3 sm:flex-row sm:items-start sm:gap-6 last:border-b-0">
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface-elevated text-muted">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
        </div>

        <div className="min-w-0">
          <div className="text-sm font-medium text-primary">{title}</div>

          {description && (
            <div className="mt-0.5 max-w-65 text-xs leading-5 text-muted">{description}</div>
          )}
        </div>
      </div>

      <div className="w-full shrink-0 self-center sm:w-90">{children}</div>
    </div>
  );
}
